import React, { type FC } from 'react';

import { Close } from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Grow,
  Typography,
  styled,
} from '@mui/material';
import clsx from 'clsx';
import { useIntl } from 'react-intl';

import { noop } from '@constants/general';

import { classes, customStyles } from './styles';
import { type ModalProps } from './types';

/** Modal window based on MUI Dialog. Extra props will be applied to the MUI Dialog. */
const Modal: FC<ModalProps> = styled(
  ({
    actions,
    children,
    className,
    'data-testid': dataTestId = 'Modal',
    dividers,
    onClose,
    open,
    preventClose,
    showCloseX = true,
    size = 'xs',
    title,
    TitleComponent = 'h6',
    fullScreen,
    fullWidth = true,
    ...dialogProps
  }: ModalProps) => {
    const intl = useIntl();
    const containerStyles = clsx(className, 'vsModal');

    const handleClose = preventClose ? noop : onClose;

    return (
      <Dialog
        TransitionComponent={Grow}
        className={containerStyles}
        data-testid={dataTestId}
        fullScreen={fullScreen}
        fullWidth={fullWidth && !fullScreen}
        maxWidth={size}
        onClose={handleClose}
        open={open}
        {...dialogProps}
      >
        {title && (
          <DialogTitle className={classes.titleContainer} component="div">
            <Typography className={classes.title} component={TitleComponent} variant="h6">
              {title}
            </Typography>

            {showCloseX && !preventClose && (
              <IconButton
                aria-label={intl.formatMessage({
                  defaultMessage: 'Close',
                  id: 'global.actions.close',
                })}
                className={classes.closeX}
                onClick={handleClose}
              >
                <Close />
              </IconButton>
            )}
          </DialogTitle>
        )}

        <DialogContent className={classes.contentContainer}>{children}</DialogContent>

        {actions && <DialogActions className={classes.actions}>{actions}</DialogActions>}
      </Dialog>
    );
  }
)(customStyles);

export default Modal;
